import { useGetRetailersForOrganizationQuery } from '@vizit/api';
import { Button, Cell, Row, type RowProps, Skeleton, Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { ButtonLevel, ButtonSize } from '@vizit/atoms';
import { FEATURE_FLAGS, RetailerKey } from '@vizit/constants';
import { useFeatureFlag } from '@vizit/feature-flags';
import { LogIn01Icon } from '@vizit/icons';
import { cva, cx } from '@vizit/theme';
import { formatDate } from '@vizit/utils';
import * as React from 'react';
import { OrganizationDialog, type OrganizationFormSchema } from '../organization-dialog';
import { rowClasses } from './organization-row.classes';
import { OrganizationRowLoading } from './organization-row.loading';

const contentClasses = cva({
  base: 'transition-opacity duration-150',
  variants: {
    disabled: {
      false: 'opacity-100',
      true: 'opacity-50',
    },
  },
});

/**
 * Props for the OrganizationRow component
 */
interface OrganizationRowProps extends RowProps<object> {
  /**
   * The account type of the organization
   *
   * @required
   * @type string
   */
  accountType?: string | null;
  /**
   * The children of the row
   *
   * @optional
   * @type React.ReactNode
   */
  children?: React.ReactNode;
  /**
   * Additional CSS classes to apply to the component
   *
   * @optional
   * @type string
   */
  className?: string;
  /**
   * The date when the organization was created (UTC) as ISO string
   *
   * @optional
   * @type string | null
   */
  createdAt?: string | null;
  /**
   * The name of the user who created the organization
   *
   * @required
   * @type string
   */
  createdBy?: string | null;
  /**
   * The organization ID
   *
   * @required
   * @type string
   */
  organizationId?: string | null;
  /**
   * Whether the row is disabled
   *
   * @optional
   * @type boolean
   * @default false
   */
  isDisabled?: boolean;
  /**
   * Whether the row is in a loading state
   *
   * @optional
   * @type boolean
   * @default false
   */
  isLoading?: boolean;
  /**
   * Whether the ICP product categories are still loading
   *
   * @optional
   * @type boolean
   * @default false
   */
  isCategoriesLoading?: boolean;
  /**
   * Whether the created by (users) data is still loading
   *
   * @optional
   * @type boolean
   * @default false
   */
  isCreatedByLoading?: boolean;
  /**
   * Array of ICP product categories for the organization
   *
   * @required
   * @type string[]
   */
  productCategories: { id: string; category_name: string }[];
  /**
   * The portfolio ID
   *
   * @required
   * @type string
   */
  portfolioId?: string | null;
  /**
   * The organization name
   *
   * @required
   * @type string
   */
  name: string;
  /**
   * Array of ICP product categories to display in the table
   *
   * @required
   * @type Record<string, string>
   */
  icpProductCategories: Record<string, string>;
  /**
   * Available retailer regions to show in the edit dialog (id → display_name)
   *
   * @optional
   * @type Record<string, string>
   */
  retailerRegions?: Record<string, string>;
  /**
   * Callback fired when the Edit button is clicked
   *
   * @optional
   * @type (data: OrganizationFormSchema) => void
   */
  onEdit?: (
    data: OrganizationFormSchema,
    defaultValues: OrganizationFormSchema,
    organizationId: string,
  ) => Promise<void> | void;
  /**
   * Callback fired when the row is clicked
   *
   * @optional
   * @type (organizationId: string) => void
   */
  onRowClick?: (organizationId: string) => void;
  /**
   * Callback fired when the View button is clicked
   *
   * @optional
   * @type (organizationId: string) => void
   */
  onView?: (organizationId: string, organizationName: string, portfolioId: string) => void;
  /**
   * Ref to the row element
   *
   * @optional
   * @type (element: HTMLTableRowElement) => void
   */
  ref?: (element: HTMLTableRowElement) => void;
}

/**
 * OrganizationRow displays organization information as a table row with cells for:
 * Name, Account Type, ICP Product Categories, Created By, Created At, and Actions.
 * Supports interactive hover/focus states and action buttons.
 *
 * Designed to work with TanStack Table and React Aria Table components.
 *
 * @param props - The props for the OrganizationRow component
 * @example
 * // Used within a Table component
 * <Table>
 *   <TableHeader>
 *     <Column>Name</Column>
 *     <Column>Account Type</Column>
 *     <Column>Categories</Column>
 *     <Column>Created By</Column>
 *     <Column>Created At</Column>
 *     <Column>Actions</Column>
 *   </TableHeader>
 *   <TableBody>
 *     <OrganizationRow
 *       organizationId="org-123"
 *       name="Acme Corporation"
 *       accountType="Enterprise"
 *       level1Categories={["Technology", "Software"]}
 *       createdBy="John Doe"
 *       createdAt="2024-01-15T10:30:00Z"
 *       onView={(id) => console.log('View:', id)}
 *       onEdit={(id) => console.log('Edit:', id)}
 *     />
 *   </TableBody>
 * </Table>
 */
function OrganizationRow(props: OrganizationRowProps) {
  const {
    organizationId = '',
    portfolioId = '',
    name,
    accountType = '',
    productCategories,
    icpProductCategories,
    retailerRegions,
    isCategoriesLoading = false,
    isCreatedByLoading = false,
    createdBy = '',
    createdAt = '',
    children,
    className,
    isLoading,
    isDisabled = false,
    onView,
    onEdit,
    onRowClick,
    ref,
    ...other
  } = props;

  const isRetailerEnabled = useFeatureFlag(FEATURE_FLAGS.APRIL_SCORING_CHANGES, false);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);

  const { data: orgRetailersData } = useGetRetailersForOrganizationQuery(
    { org_id: organizationId ?? '' },
    { enabled: isRetailerEnabled && !!organizationId && isDialogOpen },
  );

  const defaultValues = React.useMemo(
    () => ({
      organizationName: name,
      accountType: accountType ?? '',
      categories: productCategories.map(({ id }) => id),
      retailers: isRetailerEnabled ? (orgRetailersData?.map((r) => r.id) ?? [RetailerKey.AmazonUS]) : [],
    }),
    [name, accountType, productCategories, isRetailerEnabled, orgRetailersData],
  );

  const productCategoriesPrefixes = React.useMemo(() => {
    return productCategories.slice(0, 2).map(({ category_name, id }, index) => ({
      label: `${category_name}${index < productCategories.length - 1 ? ', ' : ''}`,
      id,
    }));
  }, [productCategories]);

  const productCategoriesSuffix = React.useMemo(() => {
    return productCategories.length > 2 ? `+${productCategories.length - 2} more` : '';
  }, [productCategories]);

  const handleView = React.useCallback(() => {
    if (!isDisabled && onView && organizationId && name && portfolioId) {
      onView(organizationId, name, portfolioId);
    }
  }, [isDisabled, onView, organizationId, name, portfolioId]);

  const handleEdit = React.useCallback(
    (data: OrganizationFormSchema) => {
      if (!isDisabled && onEdit && organizationId) {
        onEdit?.(data, defaultValues, organizationId);
      }
    },
    [isDisabled, onEdit, organizationId, defaultValues],
  );

  const handleRowClick = React.useCallback(() => {
    if (!isDisabled && onRowClick && organizationId) {
      onRowClick(organizationId);
    }
  }, [isDisabled, onRowClick, organizationId]);

  if (isLoading) {
    return <OrganizationRowLoading />;
  }

  return (
    <Row
      className={cx(rowClasses({ disabled: isDisabled }), className)}
      aria-label={`Organization: ${name}, Account Type: ${accountType}`}
      data-organization-id={organizationId}
      isDisabled={isDisabled}
      onAction={handleRowClick}
      ref={ref}
      {...other}
    >
      <Cell className="py-8 px-12 w-1/6">
        <div className={cx('flex items-center', contentClasses({ disabled: isDisabled }))}>
          <Text
            size={TextSize.Md}
            weight={TextWeight.Medium}
            className={cx(!isDisabled && 'group-hover:underline')}
            maxLines={2}
            showEllipsis={true}
          >
            {name}
          </Text>
        </div>
      </Cell>
      <Cell className="py-8 px-12 w-1/6">
        <div className={cx('flex items-center', contentClasses({ disabled: isDisabled }))}>
          <Text size={TextSize.Sm} color={TextColor.Secondary} showEllipsis={true}>
            {accountType}
          </Text>
        </div>
      </Cell>
      <Cell className="py-8 px-12 w-1/4">
        <div className={cx('flex items-center', contentClasses({ disabled: isDisabled }))}>
          {isCategoriesLoading ? (
            <div className="flex items-center gap-1">
              <Skeleton className="h-6 w-16" variant="square" />
              <Skeleton className="h-6 w-20" variant="square" />
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {productCategoriesPrefixes.map(({ label, id }) => (
                <Text key={id} size={TextSize.Sm} color={TextColor.Secondary} className="bg-gray-100 rounded text-xs">
                  {label}
                </Text>
              ))}
              {productCategories.length > 2 && (
                <Text size={TextSize.Sm} color={TextColor.Secondary} className="text-xs">
                  {productCategoriesSuffix}
                </Text>
              )}
            </div>
          )}
        </div>
      </Cell>
      <Cell className="py-8 px-12 w-1/6">
        <div className={cx('flex items-center', contentClasses({ disabled: isDisabled }))}>
          {isCreatedByLoading ? (
            <Skeleton className="h-5 w-24" variant="square" />
          ) : (
            <Text size={TextSize.Sm} color={TextColor.Secondary} showEllipsis={true}>
              {createdBy}
            </Text>
          )}
        </div>
      </Cell>
      <Cell className="py-8 px-12 w-1/6">
        <div className={cx('flex items-center', contentClasses({ disabled: isDisabled }))}>
          <Text size={TextSize.Sm} color={TextColor.Secondary}>
            {createdAt ? formatDate(new Date(createdAt)) : ''}
          </Text>
        </div>
      </Cell>
      <Cell className="py-8 px-12 w-1/6">
        <div className={cx('flex items-center gap-4', contentClasses({ disabled: isDisabled }))}>
          <Button
            level={ButtonLevel.Secondary}
            size={ButtonSize.Sm}
            onPress={handleView}
            isDisabled={isDisabled}
            className="text-sm font-semibold"
            iconStart={<LogIn01Icon className="text-gray-utility-400" size={20} />}
          >
            Login
          </Button>
          <OrganizationDialog
            variant="edit"
            inTable={true}
            isDisabled={isDisabled}
            id={organizationId ?? ''}
            icpProductCategories={icpProductCategories}
            retailerRegions={isRetailerEnabled ? retailerRegions : undefined}
            disabledRetailers={isRetailerEnabled ? [RetailerKey.AmazonUS] : undefined}
            onSubmit={handleEdit}
            onOpenChange={setIsDialogOpen}
            withIcon={true}
            defaultValues={defaultValues}
          />
        </div>
      </Cell>
    </Row>
  );
}

export { OrganizationRow };
export type { OrganizationRowProps };
